import { ActionType, getType } from "typesafe-actions"

import localStorage from "library/utilities/localStorage"
import { setBearerToken } from "library/utilities/token"
import * as actions from "library/common/actions/user"
import {
  DEFAULT_THEME,
  WHATS_NEW_VERSION,
  DEFAULT_COACH_MARKS,
} from "library/utilities/constants"
import { Licence } from "../types/userTypes"

type UserState = Readonly<{
  isAuthenticated: boolean
  email: string // this is the username for logging in ("testuser123")
  isRemember: boolean
  loginFailed: boolean
  failStatus: number
  newPasswordError: string | null
  oldPasswordError: string | null
  isLoadingResetPassword: boolean
  isSuccessResetPassword: boolean
  isMustResetPassword: boolean
  userName: string // this is the full name of the user ("John Doe")
  userEmail: string // this is the contact email address of the user ("john@doe.com")
  serverError: string
  handlerName: string // for the integrated view: the current iPad user
  handlerHash: string // sha256 of the current handlerName
  SSO: boolean
  knownHandlers: string[] | null
  cariesPro: boolean
  bonelossPro: boolean | null
  setBoneLossPro: boolean | null
  boneLossLite: boolean
  uploadsRemaining: number | null
  whatsNew: string | null
  theme: actions.Theme
  lastCoachMarks: actions.CoachMarkRecord
  calculus: boolean
  nervus: boolean
  impacted: boolean
  licence: Licence | null
  licenceExpire: string | null
  showDrawingMode: boolean
  boneLossOnly: boolean
  serverErrorMessage: string
  modalities: string[]
}>

const token = localStorage.getItem("access_token")
const initialState: UserState = {
  isAuthenticated: !!token,
  email: localStorage.getItem("email") || "",
  isRemember: true,
  loginFailed: false,
  failStatus: 0,
  newPasswordError: null,
  oldPasswordError: null,
  isLoadingResetPassword: false,
  isSuccessResetPassword: false,
  isMustResetPassword: false,
  SSO: false,
  userName: "",
  userEmail: "",
  handlerName: "",
  handlerHash: "",
  serverError: "",
  knownHandlers: null,
  cariesPro: false,
  bonelossPro: null,
  setBoneLossPro: null,
  boneLossLite: true,
  uploadsRemaining: null,
  whatsNew: null,
  theme: DEFAULT_THEME,
  lastCoachMarks: DEFAULT_COACH_MARKS,
  calculus: false,
  nervus: false,
  impacted: false,
  licence: null,
  licenceExpire: null,
  showDrawingMode: false,
  boneLossOnly: false,
  serverErrorMessage: "",
  modalities: [],
}
if (token) setBearerToken(token)

type UserActions = ActionType<typeof actions>

export default (state = initialState, action: UserActions): UserState => {
  switch (action.type) {
    case getType(actions.logOutAction):
      localStorage.removeItem("access_token")
      localStorage.removeItem("email")

      return {
        ...state,
        isAuthenticated: false,
        loginFailed: false,
      }

    case getType(actions.loginAction):
      return {
        ...state,
        isAuthenticated: true,
      }

    case getType(actions.loginActionSuccess):
      return {
        ...state,
        isRemember: action.payload.isRemember,
        email: action.payload.email,
        loginFailed: false,
      }

    case getType(actions.loginActionError):
      return {
        ...state,
        loginFailed: true,
        failStatus: action.payload,
      }
    case getType(actions.resetErrorAction):
      return {
        ...state,
        loginFailed: false,
        failStatus: 0,
      }

    case getType(actions.loginActionWithData):
      return {
        ...state,
      }

    case getType(actions.newPasswordErrorAction):
      return {
        ...state,
        newPasswordError: action.payload,
      }

    case getType(actions.oldPasswordErrorAction):
      return {
        ...state,
        oldPasswordError: action.payload,
      }

    case getType(actions.isLoadingResetPasswordAction):
      return {
        ...state,
        isLoadingResetPassword: action.payload,
      }

    case getType(actions.isSuccessResetPassword):
      return {
        ...state,
        isSuccessResetPassword: action.payload,
      }

    case getType(actions.setMustResetPassword):
      return {
        ...state,
        isMustResetPassword: action.payload,
      }

    case getType(actions.setUserInfo):
      const {
        fullName,
        email,
        SSO,
        knownHandlers,
        cariesPro,
        bonelossPro,
        boneLossLite,
        uploadsRemaining,
        whatsNew,
        theme,
        lastCoachMarks,
        calculus,
        nervus,
        impacted,
        licence,
        licenceExpire,
        showDrawingMode,
        boneLossOnly,
        mustResetPassword,
        modalities,
      } = action.payload

      return {
        ...state,
        userName: fullName || state.userName,
        userEmail: email || state.userEmail,
        SSO: SSO ?? state.SSO,
        knownHandlers: knownHandlers ?? state.knownHandlers,
        cariesPro: cariesPro ?? state.cariesPro,
        bonelossPro:
          state.setBoneLossPro !== null
            ? state.setBoneLossPro
            : bonelossPro === undefined
            ? state.bonelossPro
            : bonelossPro,
        boneLossLite: boneLossLite ?? state.boneLossLite,
        uploadsRemaining: uploadsRemaining ?? state.uploadsRemaining,
        whatsNew: whatsNew ?? state.whatsNew,
        theme: theme ?? state.theme,
        lastCoachMarks: lastCoachMarks ?? state.lastCoachMarks,
        calculus: calculus ?? state.calculus,
        nervus: nervus ?? state.nervus,
        impacted: impacted ?? state.impacted,
        licence: licence ?? state.licence,
        licenceExpire: licenceExpire ?? state.licenceExpire,
        showDrawingMode: showDrawingMode ?? state.showDrawingMode,
        boneLossOnly: boneLossOnly ?? state.boneLossOnly,
        isMustResetPassword: mustResetPassword ?? state.isMustResetPassword,
        modalities: modalities ?? state.modalities,
      }

    case getType(actions.setServerError):
      return {
        ...state,
        serverError: action.payload,
      }

    case getType(actions.setServerErrorMessage):
      return {
        ...state,
        serverErrorMessage: action.payload,
      }

    case getType(actions.setHandlerName):
      return {
        ...state,
        handlerName: action.payload,
      }

    case getType(actions.setHandlerHash):
      return {
        ...state,
        handlerHash: action.payload,
      }

    case getType(actions.toggleCariesPro):
      return {
        ...state,
        cariesPro: !state.cariesPro,
      }

    case getType(actions.toggleBonelossPro):
      return {
        ...state,
        bonelossPro: !state.bonelossPro,
      }

    case getType(actions.toggleDrawingMode):
      return {
        ...state,
        showDrawingMode: !state.showDrawingMode,
      }

    case getType(actions.setBonelossPro):
      return {
        ...state,
        setBoneLossPro: action.payload,
      }

    case getType(actions.setWhatsNew):
      return {
        ...state,
        whatsNew: WHATS_NEW_VERSION,
      }

    case getType(actions.setLastCoachMarks):
      const { coachMarkFeature, coachMarkIndex } = action.payload
      return {
        ...state,
        lastCoachMarks: {
          ...state.lastCoachMarks,
          [coachMarkFeature]: coachMarkIndex,
        },
      }

    default:
      return state
  }
}
